import { StatusDot } from '@/components/shared/StatusDot';
import { useNexusStore } from '@/stores/nexusStore';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Bot, Cpu, Shield, Search, Megaphone, BookOpen, GitBranch, Server, Zap, BrainCircuit } from 'lucide-react';

const AGENT_ICONS = {
  'MOA': BrainCircuit,
  'ARIA': Zap,
  'CHANGE-IQ': GitBranch,
  'ATLAS': Server,
  'SENTINL': Shield,
  'NOVA': Search,
  'CONCIERGE': Bot,
  'CAPACITOR': Cpu,
  'HERALD': Megaphone,
  'LEARN-X': BookOpen,
  'COMPLIANCE-OPS': Shield,
};

const AGENT_COLORS = {
  'MOA': '#3B82F6',
  'ARIA': '#EF4444',
  'CHANGE-IQ': '#F59E0B',
  'ATLAS': '#06B6D4',
  'SENTINL': '#EF4444',
  'NOVA': '#8B5CF6',
  'CONCIERGE': '#10B981',
  'CAPACITOR': '#F97316',
  'HERALD': '#EC4899',
  'LEARN-X': '#6366F1',
  'COMPLIANCE-OPS': '#F59E0B',
};

const AgentCard = ({ agent }) => {
  const socketStatus = useNexusStore((s) => s.agentStatuses[agent.name]);
  const mergedAgent = { ...agent, ...socketStatus };
  const isActive = mergedAgent.status === 'ACTIVE' || mergedAgent.status === 'PROCESSING';
  const Icon = AGENT_ICONS[agent.name] || Bot;
  const color = AGENT_COLORS[agent.name] || '#06B6D4';

  return (
    <div
      data-testid={`agent-card-${agent.name.toLowerCase()}`}
      className={`relative p-2.5 rounded-xl bg-[var(--nexus-bg-elevated)] border border-[var(--nexus-border)] transition-all duration-200 ${
        isActive ? 'animate-nexus-breathe' : ''
      }`}
    >
      <div className="flex items-start gap-2">
        {/* Agent icon */}
        <div
          className="flex items-center justify-center w-8 h-8 rounded-lg flex-shrink-0"
          style={{ backgroundColor: `${color}15`, color: color }}
        >
          <Icon size={15} />
        </div>

        {/* Agent info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-xs font-semibold text-[var(--nexus-text-primary)]">{agent.name}</span>
            <StatusDot
              status={mergedAgent.status === 'ACTIVE' ? 'active' : mergedAgent.status === 'PROCESSING' ? 'processing' : 'idle'}
              pulse={isActive}
              size="sm"
            />
          </div>
          <p className="text-[10px] text-[var(--nexus-text-muted)] truncate mt-0.5">{agent.description}</p>
        </div>

        {/* Confidence */}
        <div className="text-right flex-shrink-0">
          <span className="font-mono text-xs font-semibold tabular-nums" style={{ color }}>
            {mergedAgent.confidence?.toFixed(0) || 0}%
          </span>
        </div>
      </div>

      {/* Current task */}
      {mergedAgent.current_task && (
        <div className="mt-1.5 px-2 py-1 rounded-md bg-[var(--nexus-bg-base)] border border-[var(--nexus-border)]">
          <p className="text-[10px] font-mono text-[var(--nexus-accent)] truncate">{mergedAgent.current_task}</p>
        </div>
      )}

      {/* Stats row */}
      <div className="flex items-center gap-3 mt-2">
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-[var(--nexus-text-muted)]">Processed:</span>
          <span className="font-mono text-[10px] text-[var(--nexus-text-secondary)] tabular-nums">{mergedAgent.tickets_processed_today || 0}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-[var(--nexus-text-muted)]">Queue:</span>
          <span className="font-mono text-[10px] text-[var(--nexus-text-secondary)] tabular-nums">{mergedAgent.tickets_in_queue || 0}</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-[var(--nexus-text-muted)]">Rate:</span>
          <span className="font-mono text-[10px] text-[var(--nexus-success)] tabular-nums">{mergedAgent.success_rate?.toFixed(1) || 0}%</span>
        </div>
      </div>

      {/* Confidence bar */}
      <div className="mt-1.5 w-full h-1 rounded-full bg-[var(--nexus-bg-base)] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{
            width: `${mergedAgent.confidence || 0}%`,
            backgroundColor: color
          }}
        />
      </div>
    </div>
  );
};

export const AgentStatusPanel = ({ agents }) => {
  return (
    <div data-testid="agent-status-panel" className="nexus-card relative overflow-hidden">
      <div className="p-3 border-b border-[var(--nexus-border)]">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-[var(--nexus-text-primary)] tracking-wide">Agent Fleet</h2>
          <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded-md bg-[#0d3d4d] text-[var(--nexus-accent)] border border-cyan-500/30">
            {agents.filter(a => a.status === 'ACTIVE' || a.status === 'PROCESSING').length}/{agents.length} ACTIVE
          </span>
        </div>
      </div>
      <ScrollArea className="h-[480px]">
        <div className="p-2 flex flex-col gap-2">
          {agents.map((agent) => (
            <AgentCard key={agent.name} agent={agent} />
          ))}
        </div>
      </ScrollArea>
    </div>
  );
};
